import dayjs from 'dayjs';
import { formatDateRangeForQuery } from '@apps/admin/services/reports.service';
import type { identifier } from '@core/models/base';
import { authedAxios } from '@shared/axios';
import type { PagedParameters, PagedResult, DateRangeParameters } from '@shared/models/paged.collection';
import type {
  OrderStudioPaymentReportDisplayMapper,
  OrderStudioPaymentSummaryDisplayMapper,
} from '@shared/models/orders/studioAdminReports';
import { downloadResponse } from '@shared/services/download.service';

export const getStudioReportPayments = async (studioId: identifier, params: PagedParameters) => {
  if (params.endDate && typeof params.endDate !== 'string') {
    params.endDate = params.endDate.toUTCString();
  }
  if (params.startDate && typeof params.startDate !== 'string') {
    params.startDate = params.startDate.toUTCString();
  }
  return (
    await authedAxios.get<PagedResult<OrderStudioPaymentReportDisplayMapper>>(
      `/api/studioadmin/studios/${studioId}/reports/payments`,
      {
        params,
      },
    )
  ).data;
};

export const getStudioPaymentSummary = async (studioId: identifier, params: DateRangeParameters | null = null) =>
  (
    await authedAxios.get<OrderStudioPaymentSummaryDisplayMapper>(
      `/api/studioadmin/studios/${studioId}/reports/payments/summary`,
      {
        params,
      },
    )
  ).data;

export const downlodStudioPaymentReport = async (
  studioId: identifier,
  params: PagedParameters,
  dateParams: DateRangeParameters,
  jobId?: identifier,
) => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const response = await authedAxios.post(`/api/studioadmin/studios/${studioId}/reports/payments/export`, null, {
    params: {
      ...formatDateRangeForQuery({ start: dateParams.startDate as Date, end: dateParams.endDate as Date }),
      timeZone: timeZone,
      filter: params.filter,
      filterType: params.filterType,
      jobId: jobId,
    },
    responseType: 'blob',
  });
  downloadResponse(response);
};

export const downloadSubjectsReport = async (studioId: identifier, jobId: number, dateParams?: DateRangeParameters) => {
  let response;

  if (dateParams) {
    response = await authedAxios.post(`/api/studioadmin/studios/${studioId}/reports/subjects/export`, null, {
      params: {
        ...formatDateRangeForQuery({ start: dateParams.startDate as Date, end: dateParams.endDate as Date }),
        jobId: jobId,
      },
      responseType: 'blob',
    });
  } else {
    response = await authedAxios.post(`/api/studioadmin/studios/${studioId}/reports/subjects/export`, null, {
      params: {
        jobId: jobId,
      },
      responseType: 'blob',
    });
  }
  downloadResponse(response);
};

export const downloadYearbookPoseReport = async (studioId: identifier, jobId: number) => {
  const response = await authedAxios.post(`/api/studioadmin/studios/${studioId}/reports/yearbook/export`, null, {
    params: {
      jobId: jobId,
    },
    responseType: 'blob',
  });
  downloadResponse(response);
};

export interface TotalStatus {
  paidFunds: number;
  pendingFunds: number;
}

export interface StudioEarnedTotals {
  totalEarned: TotalStatus;
  monthEarned: TotalStatus;
  weekEarned: TotalStatus;
}

export const fetchStudioPaymentSummaries = async (studioId: identifier) => {
  const [totalEarned, monthEarned, weekEarned] = await Promise.all([
    getStudioPaymentSummary(studioId),
    getStudioPaymentSummary(studioId, {
      startDate: dayjs().startOf('month').format(),
      endDate: dayjs().endOf('month').format(),
    }),
    getStudioPaymentSummary(studioId, {
      startDate: dayjs().startOf('week').format(),
      endDate: dayjs().endOf('week').format(),
    }),
  ]);
  return {
    totalEarned,
    monthEarned,
    weekEarned,
  } as StudioEarnedTotals;
};
